'use client';

import { useEffect } from 'react';
import Link from 'next/link';
import * as Sentry from '@sentry/nextjs';

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // Log the error to Sentry first, then console
    Sentry.captureException(error);
    console.error('Root error boundary caught:', error);
  }, [error]);

  const isDev = process.env.NODE_ENV === 'development';

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-keiro-mist via-white to-white px-4">
      <div className="text-center max-w-2xl">
        {/* Branding */}
        <div className="mb-8">
          <Link
            href="/"
            className="inline-block text-3xl font-bold text-keiro-green-dark hover:text-keiro-green transition-colors"
          >
            Keiront
          </Link>
        </div>

        {/* Error Icon / Indicator */}
        <div className="mb-6">
          <div className="text-6xl sm:text-7xl mb-4">⚠️</div>
        </div>

        {/* Title */}
        <h1 className="text-3xl sm:text-4xl font-bold text-keiro-green-dark mb-4">
          Что-то пошло не так
        </h1>

        {/* Description */}
        <p className="text-lg text-keiro-gray-600 mb-8 leading-relaxed">
          Мы уже знаем об этом. Попробуйте ещё раз или вернитесь на главную.
        </p>

        {/* Dev Error Details (only in development) */}
        {isDev && error?.message && (
          <div className="mb-8 p-4 bg-red-50 border border-red-200 rounded-lg text-left">
            <p className="text-sm font-mono text-red-800 break-words">
              <strong>Error:</strong> {error.message}
            </p>
            {error.digest && (
              <p className="text-xs text-red-600 mt-2">
                <strong>Digest:</strong> {error.digest}
              </p>
            )}
          </div>
        )}

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <button
            onClick={reset}
            className="px-8 py-3 bg-keiro-green text-white font-semibold rounded-lg hover:bg-keiro-green-dark transition-colors shadow-sm"
          >
            Попробовать ещё раз
          </button>
          <Link
            href="/"
            className="px-8 py-3 border-2 border-keiro-green/30 text-keiro-green-dark font-semibold rounded-lg hover:bg-keiro-mist transition-colors"
          >
            На главную
          </Link>
        </div>

        {/* Additional help text */}
        <p className="mt-12 text-sm text-keiro-gray-500">
          Если проблема сохранится, свяжитесь с поддержкой
        </p>
      </div>
    </div>
  );
}
